/**
 * Casual Competitors - 关注游戏下载配置
 * @description 构建关注游戏模块的 SSE 下载参数
 */
import { omit } from 'lodash-es';
import { DOWNLOAD_CFG_KEY } from '@intelligence/v3/constants';
import commonSetting from '@intelligence/components/DownloadCommon/DownloadPage/configs/commonSetting';
import { MAX_ROWS_LIMIT } from '@intelligence/components/DownloadCommon/DownloadPage/utils';
import { shouldUseSSE } from '@intelligence/components/DownloadCommon/DownloadPage/utils/sse';

const groupByGenerators = [
  {
    formModelKey: DOWNLOAD_CFG_KEY.DATE_METHOD,
    groupByKey: 'date',
    condition: (payload: string) => payload !== 'aggregated',
  },
  {
    formModelKey: DOWNLOAD_CFG_KEY.REGION_METHOD,
    groupByKey: 'region',
    condition: (payload: string) => payload === 'region',
  },
  {
    formModelKey: DOWNLOAD_CFG_KEY.REGION_METHOD,
    groupByKey: 'market',
    condition: (payload: string) => payload === 'market',
  },
];

const groupByFormModelKeys = groupByGenerators.map((generator) => generator?.formModelKey);

const getFileName = (params: any) => {
  const startDate = params?.start_date || '';
  const endDate = params?.end_date || '';
  const fileName = params?.new_style_donwload_file_name || 'Competitors_Games';
  if (!!startDate && !!endDate) {
    return `${fileName}_${startDate}_${endDate}`;
  }
  return `${fileName}`;
};

const getDownloadApiParams = (params: any) => {
  if (!params) return {};

  const groupBys = (() => {
    const groupBys: string[] = ['game_id'];

    // 处理 Time Settings 和 Region Settings
    groupByGenerators.forEach((generator) => {
      const formModelKey = generator?.formModelKey;
      const groupByKey = generator?.groupByKey;
      const condition = generator?.condition;
      if (!!condition?.(params?.[formModelKey])) {
        groupBys.push(groupByKey);
      }
    });

    return groupBys;
  })();

  const useMarketInfo = (() => {
    if (params?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === 'market') {
      return ['market -> region', 'market -> en', 'market -> cn', 'intel_region -> en', 'intel_region -> cn'];
    }

    if (params?.[DOWNLOAD_CFG_KEY.REGION_METHOD] === 'region') {
      return ['intel_region -> en', 'intel_region -> cn'];
    }

    return [];
  })();

  const orderBy = params?.order_by || ['sensortower__daily_avg_dau desc'];

  const apiParams = {
    ...omit(params, [
      ...groupByFormModelKeys,
      DOWNLOAD_CFG_KEY.EDITION_METHOD,
      DOWNLOAD_CFG_KEY.DATA_METHOD,
      DOWNLOAD_CFG_KEY.SELECT_TOP,
      'ratio_show',
      'data_amount',
      'file_format',
      'file_name',
      'new_style_donwload_file_name',
      'ratio',
      'regionOptions',
      'hasRevenue',
      'page_game_ids',
    ]),
    group_by: groupBys,
    order_by: orderBy,
    page_size: MAX_ROWS_LIMIT,
    use_custom_fields: [
      'entity_name',
      'cover',
      'publisher',
      'country_abbre',
      'country_name_en',
      'sensortower_sub_genre',
    ],
    use_market_info: useMarketInfo,
    use_sse_page: {
      max_size: MAX_ROWS_LIMIT,
    },
    filter: {
      ...params?.filter,
    },
    // 保留 hasRevenue 参数传递到 downloadConfig
    hasRevenue: params?.hasRevenue,
    // Select Top 参数：限制下载的游戏数量
    top: {
      group_by: ['game_id'],
      order_by: orderBy,
      size: params?.select_top,
    },
  };

  // 当选择 Select All 时，删除 top 参数，下载全部数据
  if (params?.[DOWNLOAD_CFG_KEY.DATA_METHOD] === 'selectAll') {
    Reflect.deleteProperty(apiParams, 'top');
  }

  return apiParams;
};

export default (params: any) => {
  const apiParams = getDownloadApiParams(params);
  // 检查是否启用 SSE 模式
  const useSSE = shouldUseSSE(apiParams);

  return {
    ...commonSetting,
    downloadMode: 'commonConf',
    downloadConfigKey: 'casualCompetitorsKeyGames',
    downloadFetchApi: '/api/v1/intelligence_pc/studio_hub/competitor/metrics',
    downloadApiParams: apiParams,
    isUsingNewMethod: false,
    // SSE 模式下不需要 fetchOnlyOnce，因为数据是流式传输的
    fetchOnlyOnce: !useSSE,
    dataKey: undefined,
    $$fileName: getFileName(params),
    regionOptions: params?.regionOptions,
    hasRevenue: params?.hasRevenue,
    granularity: params?.granularity,
  };
};
